import {
  FilterDefinition,
  GetResultContext,
} from "components/Shared/FilterEditor/utils";
import {
  createMakeGetManyFilterResult,
  getBooleanFilterDefinition,
  getDateTimeFilterDefinition,
  getIntegerFilterDefinition,
  getSearchFilterDefinition,
  getStringFilterDefinition,
  getStringSelectFilterDefinition,
  getUuidFilterDefinition,
} from "components/Shared/FilterEditor/standardFilters";
import {
  EmailMessageFilter,
  EmailMessageParticipantType,
  ParsedReceivedEmailMessageFilter,
  ReceivedEmailMessageFilter,
} from "__generated__/gql/graphql";
import { makeEnsureType } from "utils/typescriptUtilis";
import { assertIsString } from "utils/assertions";
import SimpleCreatable from "components/Shared/Select/SimpleCreatable";
import { PARTICIPANT_TYPE_LABELS } from "components/Shared/SelectedEmail/Participants";

const filterObj = makeEnsureType<ReceivedEmailMessageFilter>();

const makeEmailMessageGetManyResult =
  createMakeGetManyFilterResult<EmailMessageFilter>();

const makeParsedReceivedEmailMessageGetManyResult =
  createMakeGetManyFilterResult<ParsedReceivedEmailMessageFilter>();

function getParticipantFilter(participantType: EmailMessageParticipantType) {
  const getResult = (value: unknown, context: GetResultContext) =>
    filterObj({
      emailMessage: makeEmailMessageGetManyResult(
        "emailMessageParticipants",
        (innerFilter) => ({
          participantType: { equalTo: participantType },
          emailAddress: { email: innerFilter },
        }),
      )(value, context),
    });

  return getStringSelectFilterDefinition({
    id: participantType.toLowerCase(),
    label: `${PARTICIPANT_TYPE_LABELS[participantType]} Email Address`,
    getResult,
    getSelectResult: getResult,
    renderSelectInput: ({ field }) => (
      <SimpleCreatable {...field} menuPortalTarget={document.body} />
    ),
  });
}

const filterDefinitions: FilterDefinition[] = [
  getSearchFilterDefinition({
    getResult: (search) => {
      assertIsString(search);
      const includesInsensitive = search;
      return filterObj({
        or: [
          { emailMessage: { subject: { includesInsensitive } } },
          {
            parsedReceivedEmailMessage: {
              messageBody: { includesInsensitive },
              receivedEmailMessageAttachments: {
                some: { originalFilename: { includesInsensitive } },
              },
            },
          },
          {
            emailMessage: {
              emailMessageParticipants: {
                some: {
                  or: [
                    { emailAddress: { email: { includesInsensitive } } },
                    { displayName: { includesInsensitive } },
                  ],
                },
              },
            },
          },
        ],
      });
    },
  }),
  getStringFilterDefinition({
    id: "subject",
    label: "Subject",
    getResult: (value, { filterVariantDefinition: { id: variantId } }) =>
      filterObj({ emailMessage: { subject: { [variantId]: value } } }),
  }),
  getIntegerFilterDefinition({
    id: "numAttachments",
    label: "Number of Attachments",
    defaultVariant: "greaterThan",
    getResult: (value, { filterVariantDefinition: { id: variantId } }) =>
      filterObj({
        parsedReceivedEmailMessage: {
          receivedEmailMessageAttachments: {
            aggregates: {
              distinctCount: { id: { [variantId]: value } },
              filter: { isRelated: { distinctFrom: true } },
            },
          },
        },
      }),
  }),
  getStringFilterDefinition({
    id: "attachment__originalFilename",
    label: "Attachment Filename",
    getResult: (value, context) =>
      filterObj({
        parsedReceivedEmailMessage: makeParsedReceivedEmailMessageGetManyResult(
          "receivedEmailMessageAttachments",
          (innerFilter) => ({ originalFilename: innerFilter }),
        )(value, context),
      }),
  }),
  getParticipantFilter(EmailMessageParticipantType.FROM),
  getParticipantFilter(EmailMessageParticipantType.TO),
  getParticipantFilter(EmailMessageParticipantType.CC),
  getParticipantFilter(EmailMessageParticipantType.BCC),
  getParticipantFilter(EmailMessageParticipantType.RECEIVER),
  getBooleanFilterDefinition({
    id: "isSpam",
    label: "Spam?",
    getResult: (value, { filterVariantDefinition: { id: variantId } }) =>
      filterObj({
        isSpam:
          variantId === "equalTo" && value
            ? { equalTo: true }
            : { distinctFrom: true },
      }),
  }),
  getDateTimeFilterDefinition({
    id: "sentAt",
    label: "Received at",
    defaultVariant: "greaterThan",
    getResult: (value, { filterVariantDefinition: { id: variantId } }) =>
      filterObj({ emailMessage: { sentAt: { [variantId]: value } } }),
  }),
  getBooleanFilterDefinition({
    id: "isCompleted",
    label: "Completed?",
    getResult: (value, { filterVariantDefinition: { id: variantId } }) =>
      filterObj({
        receivedEmailMessageCompletionExists:
          (variantId === "equalTo" && !!value) ||
          (variantId === "notEqualTo" && !value),
      }),
  }),
  getStringFilterDefinition({
    id: "messageId",
    label: "Message ID",
    getResult: (value, { filterVariantDefinition: { id: variantId } }) =>
      filterObj({ emailMessage: { messageId: { [variantId]: value } } }),
  }),
  getUuidFilterDefinition({
    id: "id",
    label: "Email ID",
    getResult: (value, { filterVariantDefinition: { id: variantId } }) =>
      filterObj({ emailMessageId: { [variantId]: value } }),
  }),
];

export default filterDefinitions;
